import React, { useState } from 'react';

interface Plateforme {
  nom: string;
  lien: string | null | undefined;
  type: 'amenage' | 'tourisme';
}

interface VignetteVanProps {
  image: string;
  nom: string;
  modele: string;
  nombreCouchages: number;
  nombrePlaces: number;
  motorisation: 'électrique' | 'hybride' | 'essence' | string;
  options: string[];
  plateformes: Plateforme[];
}

const VignetteVan: React.FC<VignetteVanProps> = ({
  image,
  nom,
  modele,
  nombreCouchages,
  nombrePlaces,
  motorisation,
  options,
  plateformes,
}) => {
  const [activeToastIndex, setActiveToastIndex] = useState<number | null>(null);

  const showToast = (index: number) => {
    setActiveToastIndex(index);
    setTimeout(() => setActiveToastIndex(null), 2000);
  };

  const getMotorisationColor = () => {
    switch (motorisation) {
      case 'électrique':
        return 'bg-green-100 text-green-700';
      case 'hybride':
        return 'bg-blue-100 text-blue-700';
      case 'essence':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 flex flex-col md:flex-row">
      {/* Image du van */}
      <div className="relative w-full md:w-1/2 h-64 md:h-auto overflow-hidden">
        <img
          src={image}
          alt={`${nom} - ${modele}`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        />
        {/* Badge motorisation */}
        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-semibold ${getMotorisationColor()}`}>
          {motorisation}
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6 flex-1">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-1">{nom}</h3>
          <p className="text-gray-600 text-sm">{modele}</p>
        </div>

        <div className="flex items-center gap-6 mb-4 text-gray-700">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-sm font-medium">{nombrePlaces} places</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-sm font-medium">{nombreCouchages} couchages</span>
          </div>
        </div>

        {options.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Équipements :</h4>
            <div className="flex flex-wrap gap-2">
              {options.map((option, index) => (
                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                  {option}
                </span>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const hasMultipleTypes = plateformes.some(p => p.type === 'amenage') && plateformes.some(p => p.type === 'tourisme');
          const renderButton = (p: Plateforme, origIndex: number) =>
            p.lien ? (
              <a
                key={origIndex}
                href={p.lien}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold text-center transition-colors duration-200"
              >
                {p.nom}
              </a>
            ) : (
              <div key={origIndex} className="flex-1 flex flex-col">
                <button
                  onClick={() => showToast(origIndex)}
                  className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-semibold text-center cursor-not-allowed"
                >
                  {p.nom}
                </button>
                {activeToastIndex === origIndex && (
                  <span className="text-xs text-gray-500 italic text-center mt-1">
                    Bientôt disponible sur cette plateforme 🚀
                  </span>
                )}
              </div>
            );

          const indexed = plateformes.map((p, i) => ({ ...p, origIndex: i }));

          if (!hasMultipleTypes) {
            const row1 = indexed.slice(0, 2);
            const row2 = indexed.slice(2);
            return (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-3">
                  {row1.map(p => renderButton(p, p.origIndex))}
                </div>
                {row2.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {row2.map(p => renderButton(p, p.origIndex))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3 opacity-0 pointer-events-none">
                    {row1.map((p, i) => (
                      <div key={i} className="flex-1 py-3 px-4 rounded-lg">&nbsp;</div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          const amenage = indexed.filter(p => p.type === 'amenage');
          const tourisme = indexed.filter(p => p.type === 'tourisme');
          return (
            <>
              <p className="text-base font-semibold text-gray-500 italic mb-2">🏕️ Version Van aménagé</p>
              <div className="flex flex-wrap gap-3">
                {amenage.map(p => renderButton(p, p.origIndex))}
              </div>
              <div className="border-t border-gray-200 mt-3 pt-3">
                <p className="text-base font-semibold text-gray-500 italic mb-2">🚗 Version véhicule de tourisme</p>
                <div className="flex flex-wrap gap-3">
                  {tourisme.map(p => renderButton(p, p.origIndex))}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default VignetteVan;
